import React, {
    createContext,
    useContext,
    useState,
    useEffect,
    ReactNode,
    useMemo,
    useCallback,
} from "react";
import { Favorite } from "@interfaces/favorite.interface";
import { DA_NewProduct } from "@danishagro/shared/src/interfaces/productv2.interface";
import { useFavoritesApi } from "../hooks/api/useFavoriteList.hook";

interface FavoriteContextType {
    favorites: Favorite[];
    loading: boolean;
    addFavorite: (productId: string, variantId: string) => Promise<void>;
    removeFavorite: (productId: string, variantId: string) => Promise<void>;
    isFavoriteProduct: (product: DA_NewProduct, productVariantIds?: string[]) => boolean;
}

const FavoriteContext = createContext<FavoriteContextType | undefined>(undefined);

export const FavoriteProvider = ({ children }: { children: ReactNode }) => {
    const [favorites, setFavorites] = useState<Favorite[]>([]);
    const [loading, setLoading] = useState(false);
    const {
        fetchFavorites,
        fetchCookieFavorites,
        addFavorite,
        removeFavorite,
        removeCookieFavorite,
        addCookieFavorite,
    } = useFavoritesApi();

    const loggedIn = useMemo(() => globalThis.isAuthenticated ?? false, []);

    // Fetch favorites on mount or when login status changes
    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const list = loggedIn ? await fetchFavorites() : await fetchCookieFavorites();

                setFavorites(list);
            } catch (e) {
                console.error(e);
            } finally {
                setLoading(false);
            }
        })();
    }, [fetchCookieFavorites, fetchFavorites, loggedIn]);

    const handleAdd = async (productId: string, variantId: string) => {
        const updated = loggedIn
            ? await addFavorite(productId, variantId)
            : await addCookieFavorite(productId, variantId);

        setFavorites(updated);
    };

    const handleRemove = async (productId: string, variantId: string) => {
        const updated = loggedIn
            ? await removeFavorite(productId, variantId)
            : await removeCookieFavorite(productId, variantId);

        setFavorites(updated);
    };

    const isFavoriteProduct = useCallback(
        (product: DA_NewProduct, productVariantIds?: string[]) => {
            if (!favorites && favorites?.length === 0) return false;

            if (product?.hasVariants && productVariantIds?.length > 0) {
                return favorites?.some((fav) => productVariantIds?.includes(fav.productVariantId));
            }

            return favorites?.some(
                (fav) =>
                    fav.productVariantId === product.variantEntityId ||
                    fav.productId === product.entityId
            );
        },
        [favorites]
    );

    return (
        <FavoriteContext.Provider
            value={{
                favorites,
                loading,
                addFavorite: handleAdd,
                removeFavorite: handleRemove,
                isFavoriteProduct,
            }}
        >
            {children}
        </FavoriteContext.Provider>
    );
};

export const useFavorites = () => {
    const context = useContext(FavoriteContext);
    if (!context) {
        throw new Error("useFavorites must be used within a FavoriteProvider");
    }
    return context;
};
